import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { Link } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import React, {
  useLayoutEffect, useRef, useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import Layout from '@layout';
import { LocationProps } from '@models/navigation';
import { ContentType, PageContextData, SlugPrefix } from '@models/pages';
import {
  formatDateMedium, formatSeoDescription, ShowEventSections,
} from '@services';
import {
  collection,
  dbClient,
  getDocs,
  query,
  where,
} from '@services/firebase.services';
import { H1 } from '@styles/components';
import RelatedVideos from './RelatedVideos';
import ChdButton from '../Buttons/ChdButton';
import ChdButtonGroup from '../Buttons/ChdButtonGroup';
import ShareButton from '../Buttons/ShareButton';
import TextAlertsButton from '../Buttons/TextAlertsButton';
import VideoButtonGroup from '../Buttons/VideoButtonGroup';
import { PublicChatWindow } from '../Chat';
import SendChatMessage from '../Chat/SendChatMessage';
import LinkChd from '../LinkChd';
import VideoPlayer from '../players/VideoPlayer';
import VideoPlayerThumbnail from '../players/VideoPlayerThumbnail';
import PrintTranscript from '../PrintTranscript';
import ReadMoreLess from '../ReadMoreLess';
import ResourceLinks from '../ResourceLinks';
import SEO from '../SEO';
import VideoDate from '../VideoDate';

// eslint-disable-next-line max-len
const chatMessagesCollectionName: string = process.env.GATSBY_FIREBASE_CHDTV_CHATMESSAGES_COLLECTION_NAME!;
// eslint-disable-next-line max-len
const liveBloggingCollectionName: string = process.env.GATSBY_FIREBASE_CHDTV_LIVEBLOGGINGMESSAGES_COLLECTION_NAME!;

type Props = {
  pageContext: PageContextData,
  data: any,
  episode: Boolean,
  location: LocationProps;
}

function EpisodeOrEventVideo({
  pageContext, data, episode = true, location,
}: Props) {
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });
  const [live, setCurrentPageLive] = useState(false);
  const [liveChatActive, setLiveChatActive] = useState<boolean>(true);
  const [liveBlogActive, setLiveBlogActive] = useState<boolean>(false);
  const [loadVideoPlayer, setLoadVideoPlayer] = useState<boolean>(false);
  const [showTranscript, setShowTranscript] = useState<boolean>(false);

  const handleShowTranscript = () => setShowTranscript(!showTranscript);
  const stickyVideo = useRef<any>();
  const { globalCmsButtons, stringTranslations, lang } = pageContext;
  const showOrEvent = episode ? data.show : data.event;
  const episodeOrEventVideo = episode ? data.episode : data.eventVideo;
  const resourceLinksTitle = stringTranslations?.Resources[episodeOrEventVideo.node_locale];
  const { defenderLink, podcastListenUrl, transcript } = episodeOrEventVideo;

  const relatedVideos = episode
    ? (data.recentEpisodes?.nodes.filter((item: any) => item.show !== null) || [])
    : (data.recentEventVideos?.nodes.filter((item: any) => item.event !== null) || []);

  const uniqueRelatedVideos = [
    ...new Map(relatedVideos.map((video:any) => [video.title, video])).values(),
  ].slice(0, 4);

  const pageLocales: { lang: string; slug: string }[] = data.childPageLocales.nodes.reduce(
    (pageLocaleAcc: any[], pageLocale: any) => {
      const parentLocale = data.parentPageLocales.nodes.find(
        (locale: any) => locale.node_locale === pageLocale.node_locale,
      );
      if (parentLocale) {
        const locale = {
          lang: pageLocale.node_locale,
          slug: `${pageLocale.node_locale !== 'en'
            ? `/${pageLocale.node_locale}`
            : ''
          }${episode ? SlugPrefix.ChdTvShows : SlugPrefix.ChdTvEvents}${parentLocale.slug}/${pageLocale.slug}/`,
        };
        pageLocaleAcc.push(locale);
      }
      return pageLocaleAcc;
    },
    [],
  );
  const showOrEventUrl = `${episodeOrEventVideo.node_locale !== 'en' ? `/${episodeOrEventVideo.node_locale}` : ''}${episode ? SlugPrefix.ChdTvShows : SlugPrefix.ChdTvEvents}${showOrEvent.slug}/`;
  const [hasLiveBloggingMsgs, setHasLiveBloggingMsgs] = useState<boolean>(false);
  const videoPublishDateAndTime = formatDateMedium(episodeOrEventVideo?.publishDate || episodeOrEventVideo.createdAt, episodeOrEventVideo?.node_locale) ?? '';
  // eslint-disable-next-line no-param-reassign
  pageContext.locales = pageLocales;

  const hosts = episode ? showOrEvent.showHosts?.map((host: any) => host.name).join(', ') : showOrEvent.eventHosts?.map((host: any) => host.name).join(', ');
  const videoThumbnailImage = episodeOrEventVideo?.seoImage?.gatsbyImageData
    ? getImage(episodeOrEventVideo.seoImage.gatsbyImageData)
    : null;

  const donateLinkDetails = globalCmsButtons.Donate[lang];
  const signUpLinkDetails = globalCmsButtons['Free Sign-Up'][lang];
  if (defenderLink && defenderLink?.title) {
    defenderLink.title = globalCmsButtons['Read In The Defender'][lang].title;
  }

  const mappedRelatedVideos = uniqueRelatedVideos
    ?.filter((video: any) => video.visible)
    .map((video: any) => {
      const videoSlug = video.internal.type === ContentType.ContentfulEpisode ? `${SlugPrefix.ChdTvShows}${video.show[0].slug}/${video.slug}/` : `${SlugPrefix.ChdTvEvents}${video.event[0].slug}/${video.slug}`;

      return ({
        ...video,
        slug: video.node_locale === 'en' ? `${videoSlug}` : `/${video.node_locale}${videoSlug}/`,
      });
    });

  // eslint-disable-next-line consistent-return

  useLayoutEffect(() => {
    const getLiveChatEntries = async () => {
      const { data: liveEntriesResp } = await axios.get('/api/chat/get-live-data');
      if (liveEntriesResp.liveVideos?.length === 0) {
        return;
      }
      const liveItems = liveEntriesResp?.liveVideos.filter((v) => v.imageUrl && v.slug);
      setCurrentPageLive(
        liveItems?.map((item:any) => item.id).includes(episodeOrEventVideo.contentful_id),
      );
    };
    getLiveChatEntries();

    if (live && showOrEvent?.liveBlogging) setLiveBlogActive(true);
    if (isMobile) {
      const stickyVideoCleanup = (checkStickyVideo: any) => {
        if (isMobile) {
          const videoContainer = document.getElementById('videoContainer');
          videoContainer?.removeEventListener('scroll', checkStickyVideo);
        }
      };
      const videoContainer = document.getElementById('videoContainer');
      const chatContainer = document.getElementById('chatContainer');
      const initialVideoFromTop = stickyVideo.current.offsetTop;
      const checkStickyVideo = () => {
        if (window.scrollY > initialVideoFromTop + 200) {
          videoContainer?.classList.add(
            'fixed',
            'top-[55px]',
            'right-[25px]',
            'w-1/2',
            'z-[99999]',
          );
          chatContainer?.classList.add('mt-36');
        } else {
          videoContainer?.classList.remove(
            'fixed',
            'top-[55px]',
            'right-[25px]',
            'w-1/2',
            'z-[99999]',
          );
          chatContainer?.classList.remove('mt-36');
        }
      };
      window.addEventListener('scroll', checkStickyVideo);
      return stickyVideoCleanup(checkStickyVideo);
    }

    const msgQuery = query(
      collection(dbClient, liveBloggingCollectionName),
      where('contentId', '==', episodeOrEventVideo.contentful_id),
    );
    const checkLiveBloggingMessages = async (q: any) => {
      const liveBloggingMsgs: any = await getDocs(q);
      setHasLiveBloggingMsgs(liveBloggingMsgs?.docs.length > 0);
    };
    checkLiveBloggingMessages(msgQuery);
  }, [, live]);

  return (
    <Layout
      location={location}
      lang={pageContext.lang}
      locales={pageContext.locales}
      page={pageContext}
    >
      <SEO
        title={episodeOrEventVideo.title}
        image={
          episodeOrEventVideo.seoImage?.gatsbyImageData.images.fallback.src
          || showOrEvent.heroImage?.image.url
        }
        lang={lang}
        locales={pageContext.locales}
        description={formatSeoDescription(
          episodeOrEventVideo.description?.childMarkdownRemark.html
          || showOrEvent.description?.childMarkdownRemark.html,
        )}
      />
      <main className="mx-5 xl:mx-[70px] flex flex-col items-center">
        <div
          className="max-w-[1300px]"
        >
          <div
            className="w-full"
          >
            <Link
              type="button"
              to={showOrEventUrl}
              className="flex ml-[-5px] items-center w-fit my-4 text-chdGreen-light hover:text-chdGreen-hover"
            >
              <ChevronLeftIcon className="w-[1.25rem]" />
              <div
                className="tracking-[2.24px] font-semibold text-[16px] whitespace-nowrap uppercase"
              >
                {episode ? ShowEventSections.ShowHome : ShowEventSections.EventHome}
              </div>
            </Link>
          </div>
          <div className="md:flex md:gap-x-8 xl:gap-x-20">
            <section className="md:flex md:flex-col md:basis-[69%] text-white text-[14px]">
              <div className="flex uppercase justify-start mb-2.5 lg:hidden">
                {videoPublishDateAndTime && <VideoDate date={videoPublishDateAndTime} />}
                <ShareButton
                  globalCmsButtons={globalCmsButtons}
                  lang={lang}
                />
                {episodeOrEventVideo.podcastListenUrl && (
                <LinkChd
                  linkStyles="font-semibold tracking-wider text-center uppercase text-chdGreen-light hover:text-chdGreen-hover"
                  href={episodeOrEventVideo.podcastListenUrl}
                >
                  {globalCmsButtons['Spotify Listen'][lang].title}
                </LinkChd>
                )}
              </div>
              <div
                id="videoContainer"
                ref={stickyVideo}
              >
                {loadVideoPlayer ? (
                  <VideoPlayer
                    playbackId={episodeOrEventVideo.videoPlaybackId}
                    playerName="episode-player"
                    video={episodeOrEventVideo}
                    autoPlay
                  />
                ) : (
                  <VideoPlayerThumbnail
                    altText={episodeOrEventVideo.title}
                    btnOnclick={() => setLoadVideoPlayer(true)}
                    image={videoThumbnailImage}
                    video={episodeOrEventVideo}
                  />
                )}
              </div>
              <VideoButtonGroup
                episode={episode}
                globalCmsButtons={globalCmsButtons}
                handleShowTranscript={handleShowTranscript}
                hasLiveBloggingMsgs={hasLiveBloggingMsgs}
                lang={lang}
                liveChatActive={liveChatActive}
                liveBlogActive={liveBlogActive}
                setLiveBlogActive={setLiveBlogActive}
                setLiveChatActive={setLiveChatActive}
                podcastListenUrl={podcastListenUrl}
                stringTranslations={stringTranslations}
                transcript={transcript}
                videoPublishDateAndTime={videoPublishDateAndTime}
              />
              {transcript && showTranscript && (
              <PrintTranscript
                handleShowTranscript={handleShowTranscript}
                lang={lang}
                stringTranslations={stringTranslations}
                transcriptTitle={episodeOrEventVideo.title}
                transcript={transcript}
              />
              )}
              <div
                id="chatContainer"
                className={`flex w-full ${liveChatActive && 'min-h-96'} mt-4 md:mt-0 md:hidden`}
              >
                {liveChatActive && (
                  <div className="flex flex-col justify-start w-[calc(100vw-40px)] h-full p-2 bg-black border border-darkGreyBlue">
                    <h4 className="flex font-bold text-white uppercase">
                      {live
                        ? `${stringTranslations?.['live-chat'][
                          episodeOrEventVideo.node_locale
                        ]
                        }`
                        : `${stringTranslations?.chat[episodeOrEventVideo.node_locale]}`}
                    </h4>
                    <div
                      className={`grow overflow-auto ${live ? 'max-h-[250px]' : 'max-h-[300px]'
                      }`}
                    >
                      <PublicChatWindow
                        contentId={episodeOrEventVideo.contentful_id}
                        isForLiveBlogging={false}
                        dbCollectionName={chatMessagesCollectionName}
                      />
                    </div>
                    <div className="flex items-end grow">
                      <SendChatMessage
                        contentId={episodeOrEventVideo.contentful_id}
                        dbCollectionName={chatMessagesCollectionName}
                        isForLiveBlogging={false}
                        isFromModerator={false}
                        lang={episodeOrEventVideo.node_locale}
                        stringTranslations={stringTranslations}
                      />
                    </div>
                  </div>
                )}
              </div>
              {liveBlogActive && (
                <div id="liveBloggingContainer" className="mt-2">
                  {live && showOrEvent?.liveBlogging && (
                    <div className="flex flex-col justify-start h-full p-2 bg-white">
                      <h4 className="flex font-bold uppercase text-chdBlue">
                        {
                          stringTranslations?.live_blogging[
                            episodeOrEventVideo.node_locale
                          ]
                        }
                      </h4>
                      <div className="grow overflow-auto max-h-[300px]">
                        <PublicChatWindow
                          contentId={episodeOrEventVideo.contentful_id}
                          dbCollectionName={liveBloggingCollectionName}
                          isForLiveBlogging
                        />
                      </div>
                    </div>
                  )}
                  {!live && hasLiveBloggingMsgs && (
                    <div className="flex flex-col justify-start h-full p-2 bg-white">
                      <h4 className="flex font-bold uppercase text-chdBlue">
                        {
                          stringTranslations?.live_blogging[
                            episodeOrEventVideo.node_locale
                          ]
                        }
                        <span className="inline-block p-1 ml-2 text-sm font-normal text-black capitalize">
                          <em>
                            {
                              stringTranslations?.live_blogging_closed[
                                episodeOrEventVideo.node_locale
                              ]
                            }
                          </em>
                        </span>
                      </h4>
                      <div className="grow overflow-auto h-[300px]">
                        <PublicChatWindow
                          contentId={episodeOrEventVideo.contentful_id}
                          dbCollectionName={liveBloggingCollectionName}
                          isForLiveBlogging
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mb-6">
                <H1 id="content-title">{episodeOrEventVideo.title}</H1>
                <ReadMoreLess
                  descriptionStyles="flex flex-col w-full md:flex md:flex-auto"
                  text={episodeOrEventVideo.description?.childMarkdownRemark.html}
                  stringTranslations={stringTranslations}
                  lang={episodeOrEventVideo.node_locale}
                />
                {hosts && (
                  <div>
                    <p
                      className="mb-[5px] text-[16px] leading-[22px]"
                      id="content-hosts"
                    >
                      {stringTranslations?.show_host[episodeOrEventVideo.node_locale]}
                      :
                      {' '}
                      {hosts}
                    </p>
                  </div>
                )}
                <p className="text-[16px] leading-[22px] mt-4">
                  <em>
                    {
                      stringTranslations?.show_disclaimer[
                        episodeOrEventVideo.node_locale
                      ]
                    }
                  </em>
                </p>
                <ChdButtonGroup className="my-12">
                  <ChdButton
                    link={donateLinkDetails}
                    colorTheme="blue"
                  />
                  <TextAlertsButton
                    lang={lang}
                    globalCmsButtons={globalCmsButtons}
                    className="px-2 py-4 bg-chdGreen-light hover:bg-chdGreen-hover"
                  />
                  <ChdButton
                    link={signUpLinkDetails}
                    colorTheme="green"
                    linkStyles="xl:bg-chdBlue-light xl:hover:bg-[#3FB6E8] xl:border-[#0080A3] "
                  />
                  {defenderLink && (
                    <ChdButton
                      link={defenderLink}
                      colorTheme="blue"
                      linkStyles="xl:bg-chdGreen-light xl:hover:bg-chdGreen-hover"
                    />
                  )}
                </ChdButtonGroup>
                {ResourceLinks(
                  episodeOrEventVideo.resourceLinks,
                  resourceLinksTitle,
                )}
              </div>
            </section>
            <section className="md:flex md:flex-col md:basis-[31%] w-full">
              {liveChatActive && (
                <div
                  id="chatContainer"
                  className="hidden w-full mt-4 mb-16 min-h-96 md:mt-0 md:flex"
                >
                  <div className="flex flex-col justify-start md:w-[275px] lg:w-[369px] h-full p-2 bg-black border border-darkGreyBlue">
                    <h4 className="flex font-bold text-white uppercase">
                      {live
                        ? `${stringTranslations?.['live-chat'][
                          episodeOrEventVideo.node_locale
                        ]
                        }`
                        : `${stringTranslations?.chat[episodeOrEventVideo.node_locale]}`}
                    </h4>
                    <div
                      className={`grow overflow-auto ${live ? 'max-h-[250px]' : 'max-h-[300px]'
                      }`}
                    >
                      <PublicChatWindow
                        contentId={episodeOrEventVideo.contentful_id}
                        isForLiveBlogging={false}
                        dbCollectionName={chatMessagesCollectionName}
                      />
                    </div>
                    <div className="flex items-end grow">
                      <SendChatMessage
                        contentId={episodeOrEventVideo.contentful_id}
                        dbCollectionName={chatMessagesCollectionName}
                        isForLiveBlogging={false}
                        isFromModerator={false}
                        lang={episodeOrEventVideo.node_locale}
                        stringTranslations={stringTranslations}
                      />
                    </div>
                  </div>
                </div>
              )}
              {mappedRelatedVideos.length && (
                <RelatedVideos
                  relatedVideos={mappedRelatedVideos}
                  title="Related Videos"
                />
              )}
            </section>
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default EpisodeOrEventVideo;
